import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ProductKey } from "../../../shared/products/keys";

export interface IdTokenClaims {
  aud?: string;
  iss?: string;
  iat?: number;
  nbf?: number;
  exp?: number;
  sub?: string;
  name?: string;
  email?: string;
  emails?: string[];
  tfp?: string;
  nonce?: string;
  scp?: string;
  azp?: string;
  ver?: string;
  [key: string]: unknown;
}

export interface UserPermissions {
  isPlatformAdmin: boolean;
  isTenantAdmin: boolean;
  isInstanceAdmin: boolean;
}

export interface ErrorInfo {
  isError: boolean;
  errorMessage: string;
  errorCode: string;
}

export interface CapabilityPermissions {
  allowView: boolean;
  allowEdit: boolean;
  allowDelete: boolean;
  /**
   * Whether this instance may use SkyAgent. Decided on the server from
   * `public.skyagent_enabled_instances`; the UI only reads it.
   */
  skyAgentEnabled: boolean;
  /** True once resolve-instance (or the full-access fallback) has answered. The route guard waits on it. */
  productsResolved: boolean;
  /** Products the current instance has (spec §3). Empty until resolved. */
  products: ProductKey[];
  /** The caller's role per product they hold. Empty until resolved. */
  productRoles: Partial<Record<ProductKey, { id: string; name: string; label: string }>>;
}

export interface InstanceSettings {
  cultureName: string | undefined;
  timezone: string | undefined;
}

export interface AuthState {
  initialized: boolean;
  accessToken: string | undefined;
  idTokenClaims: IdTokenClaims | undefined;
  permissions: UserPermissions;
  capabilities: CapabilityPermissions;
  error: ErrorInfo;
  isFullAccess: boolean;
  isAuthenticated: boolean;
  instanceSettings: InstanceSettings;
}

const initialState: AuthState = {
  initialized: false,
  accessToken: undefined,
  idTokenClaims: undefined,
  permissions: {
    isPlatformAdmin: false,
    isTenantAdmin: false,
    isInstanceAdmin: false,
  },
  capabilities: {
    allowView: false,
    allowEdit: false,
    allowDelete: false,
    // Off until the server says otherwise, so a failed or pending resolve hides
    // SkyAgent rather than flashing it to an instance that may not have it.
    skyAgentEnabled: false,
    productsResolved: false,
    products: [],
    productRoles: {},
  },
  error: {
    isError: false,
    errorMessage: "",
    errorCode: "",
  },
  isFullAccess: false,
  isAuthenticated: false,
  instanceSettings: {
    cultureName: undefined,
    timezone: undefined,
  },
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAccessToken: (state, action: PayloadAction<string | undefined>) => {
      state.accessToken = action.payload;
    },

    setIdTokenClaims: (state, action: PayloadAction<IdTokenClaims | undefined>) => {
      state.idTokenClaims = action.payload;
    },

    setTokens: (
      state,
      action: PayloadAction<{
        accessToken: string | undefined;
        idTokenClaims: IdTokenClaims | undefined;
      }>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.idTokenClaims = action.payload.idTokenClaims;
    },

    clearTokens: (state) => {
      state.accessToken = undefined;
      state.idTokenClaims = undefined;
      state.isAuthenticated = false;
    },

    setPermissions: (state, action: PayloadAction<UserPermissions>) => {
      state.permissions = action.payload;
    },

    setIsPlatformAdmin: (state, action: PayloadAction<boolean>) => {
      state.permissions.isPlatformAdmin = action.payload;
    },

    setIsTenantAdmin: (state, action: PayloadAction<boolean>) => {
      state.permissions.isTenantAdmin = action.payload;
    },

    setIsInstanceAdmin: (state, action: PayloadAction<boolean>) => {
      state.permissions.isInstanceAdmin = action.payload;
    },

    setError: (state, action: PayloadAction<ErrorInfo>) => {
      state.error = action.payload;
    },

    setIsError: (
      state,
      action: PayloadAction<boolean | { isError: boolean; message?: string; code?: string }>,
    ) => {
      if (typeof action.payload === "boolean") {
        state.error.isError = action.payload;
        if (!action.payload) {
          state.error.errorMessage = "";
          state.error.errorCode = "";
        }
      } else {
        state.error.isError = action.payload.isError;
        state.error.errorMessage = action.payload.message ?? "";
        state.error.errorCode = action.payload.code ?? "";
      }
    },

    setIsFullAccess: (state, action: PayloadAction<boolean>) => {
      state.isFullAccess = action.payload;
    },

    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },

    setCapabilities: (state, action: PayloadAction<CapabilityPermissions>) => {
      state.capabilities = action.payload;
    },

    /** A product was disabled mid-session (PRODUCT_DISABLED): drop it so nav and guards follow. */
    clearProduct: (state, action: PayloadAction<ProductKey>) => {
      state.capabilities.products = state.capabilities.products.filter((p) => p !== action.payload);
      const { [action.payload]: _dropped, ...rest } = state.capabilities.productRoles;
      state.capabilities.productRoles = rest;
    },

    setAllowView: (state, action: PayloadAction<boolean>) => {
      state.capabilities.allowView = action.payload;
    },

    setAllowEdit: (state, action: PayloadAction<boolean>) => {
      state.capabilities.allowEdit = action.payload;
    },

    setAllowDelete: (state, action: PayloadAction<boolean>) => {
      state.capabilities.allowDelete = action.payload;
    },

    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
    },

    signOut: (state) => {
      state.accessToken = undefined;
      state.idTokenClaims = undefined;
      state.permissions = {
        isPlatformAdmin: false,
        isTenantAdmin: false,
        isInstanceAdmin: false,
      };
      state.capabilities = {
        allowView: false,
        allowEdit: false,
        allowDelete: false,
        skyAgentEnabled: false,
        productsResolved: false,
        products: [],
        productRoles: {},
      };
      state.error = {
        isError: false,
        errorMessage: "",
        errorCode: "",
      };
      state.isFullAccess = false;
      state.isAuthenticated = false;
      state.initialized = false;
    },

    tenantChange: (state) => {
      state.permissions = {
        isPlatformAdmin: state.permissions.isPlatformAdmin,
        isTenantAdmin: false,
        isInstanceAdmin: false,
      };
    },

    instanceChange: (state) => {
      state.permissions.isInstanceAdmin = false;
    },

    setInstanceSettings: (state, action: PayloadAction<InstanceSettings>) => {
      state.instanceSettings = action.payload;
    },

    setInstanceCultureName: (state, action: PayloadAction<string | undefined>) => {
      state.instanceSettings.cultureName = action.payload;
    },

    setInstanceTimezone: (state, action: PayloadAction<string | undefined>) => {
      state.instanceSettings.timezone = action.payload;
    },
  },
});

export const {
  setAccessToken,
  setIdTokenClaims,
  setTokens,
  clearTokens,
  setPermissions,
  setIsPlatformAdmin,
  setIsTenantAdmin,
  setIsInstanceAdmin,
  setError,
  setIsError,
  setIsFullAccess,
  setIsAuthenticated,
  setCapabilities,
  clearProduct,
  setAllowView,
  setAllowEdit,
  setAllowDelete,
  setInitialized,
  signOut,
  tenantChange,
  instanceChange,
  setInstanceSettings,
  setInstanceCultureName,
  setInstanceTimezone,
} = authSlice.actions;

export default authSlice.reducer;
